import React from 'react';
import { Box, Grid, Skeleton } from '@mui/material';
import { HEADCOUNT_METRICS_CARD_ESTIMATED_HEIGHT_PX } from '../../constants/headcountMetrics';
import { SKELETON_ANIMATION, skeletonTintSx } from '../common/loading/skeletonTokens';
import { MetricCardsRowSkeleton } from '../common/loading/MetricCardsRowSkeleton';

/** Lazy-chunk fallback: KPI strip matches `UtilizationMetricCardsSection` loading state */
export const UtilizationMetricCardsSuspenseFallback: React.FC = () => (
  <Box sx={{ mb: 2 }}>
    <MetricCardsRowSkeleton count={8} height={100} />
  </Box>
);

/** Headcount: three chart columns, matches `HeadcountMetricsSection` grid */
export const UtilizationHeadcountSectionSkeleton: React.FC = () => (
  <Box>
    <Skeleton variant="text" width={220} height={32} sx={{ mb: 1, ...skeletonTintSx }} />
    <Skeleton variant="rectangular" width={60} height={3} sx={{ mb: 2, borderRadius: 1, ...skeletonTintSx }} />
    <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
      {[1, 2, 3].map((k) => (
        <Grid item xs={12} md={4} key={k} sx={{ display: 'flex', minWidth: 0 }}>
          <Skeleton
            variant="rounded"
            animation={SKELETON_ANIMATION}
            height={HEADCOUNT_METRICS_CARD_ESTIMATED_HEIGHT_PX}
            sx={{ width: '100%', borderRadius: 2, ...skeletonTintSx }}
          />
        </Grid>
      ))}
    </Grid>
  </Box>
);

export const UtilizationHeadcountAllocationSkeleton: React.FC = () => (
  <Skeleton
    variant="rounded"
    animation={SKELETON_ANIMATION}
    height={HEADCOUNT_METRICS_CARD_ESTIMATED_HEIGHT_PX}
    sx={{ width: '100%', borderRadius: 2, ...skeletonTintSx }}
  />
);

/** Single chart slot in headcount row */
export const UtilizationHeadcountChartSkeleton: React.FC = () => (
  <Skeleton
    variant="rounded"
    animation={SKELETON_ANIMATION}
    height={HEADCOUNT_METRICS_CARD_ESTIMATED_HEIGHT_PX}
    sx={{ width: '100%', borderRadius: 2, ...skeletonTintSx }}
  />
);

/** `UtilizationAnalysisSection` + stacked charts footprint */
export const UtilizationAnalysisSectionSkeleton: React.FC = () => (
  <Box>
    <Skeleton variant="text" width={200} height={28} sx={{ mb: 1, ...skeletonTintSx }} />
    <Skeleton variant="rectangular" width={60} height={3} sx={{ mb: 2, borderRadius: 1, ...skeletonTintSx }} />
    <Skeleton variant="text" width="55%" height={20} sx={{ mb: 2, ...skeletonTintSx }} />
    <Skeleton variant="rounded" height={380} sx={{ borderRadius: 2, ...skeletonTintSx }} />
  </Box>
);

/** `OrgAnalytics` table block (~600px scroll region) */
export const UtilizationOrgAnalyticsSkeleton: React.FC = () => (
  <Box>
    <Skeleton variant="text" width={340} height={28} sx={{ mb: 2, ...skeletonTintSx }} />
    <Skeleton variant="rounded" height={520} sx={{ borderRadius: 2, ...skeletonTintSx }} />
  </Box>
);

/** `BenchDetails` dual-table layout */
export const UtilizationBenchSkeleton: React.FC = () => (
  <Box>
    <Skeleton variant="text" width={200} height={32} sx={{ mb: 2, ...skeletonTintSx }} />
    <Box sx={{ display: 'flex', gap: 2, minHeight: 400, flexDirection: { xs: 'column', md: 'row' } }}>
      <Skeleton variant="rounded" sx={{ flex: { xs: '1 1 auto', md: '0 0 70%' }, minHeight: 320, borderRadius: 2, ...skeletonTintSx }} />
      <Skeleton variant="rounded" sx={{ flex: { xs: '1 1 auto', md: '0 0 30%' }, minHeight: 320, borderRadius: 2, ...skeletonTintSx }} />
    </Box>
  </Box>
);

/** `EmployeeDetails` header + wide table */
export const UtilizationEmployeeSectionSkeleton: React.FC = () => (
  <Box sx={{ p: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
      <Skeleton variant="circular" width={28} height={28} sx={skeletonTintSx} />
      <Skeleton variant="text" width={200} height={36} sx={skeletonTintSx} />
    </Box>
    <Skeleton variant="text" width="50%" height={22} sx={{ mb: 3, ...skeletonTintSx }} />
    <Skeleton variant="rounded" height={560} sx={{ borderRadius: 2, ...skeletonTintSx }} />
  </Box>
);
